import { ID_PART_PATTERN, MAX_FIXTURE_ID_LENGTH, VERSION_SLUG_PATTERN } from "./instance-identity.js";
import { z } from "zod";
//#region src/app-introspection.schemas.ts
/**
* Wire schemas for the CP → DP app-introspection surface (spine V-2, seam S2):
*
*   GET  /internal/app-introspection/streams       — per-principal JetStream state
*   GET  /internal/app-introspection/dependencies  — expected vs actual, per principal
*   GET  /internal/app-introspection/config        — env-var NAMES + comparators (V-2a)
*   POST /internal/app-introspection/probe         — one fenced in-cluster health probe (V-2b)
*   GET  /internal/app-introspection/database      — per-table Postgres statistics over a window
*   GET  /internal/observability/logs/aggregate    — the fifth verb, a COUNT (§12.2)
*
* ⛔ ONE declaration, BOTH sides. core-api parses its OWN response against these
* before sending (`AppIntrospectionSchemas`, §7.2) and site-admin-api's adapter
* parses the body it receives. A shape that only one side believes in is the
* defect the `2033c69dc` "fakes that cannot drift" change closed on the fake
* side; this closes it on the producer side.
*
* ⛔ **THERE IS NO `value` FIELD ANYWHERE IN THIS FILE, AND THAT IS THE POINT
* (V-2a / security-audit A2).** A config value is not redacted in transit — it is
* STRUCTURALLY UNABLE TO BE SENT, because the type it would have to inhabit does
* not exist. `valueHmac` is a comparator ("did it change; do two fixtures
* agree"), which is the entire job. Adding a `value` field here is a blocking
* review finding, not a feature.
*
* ⛔ **THERE IS NO url / host / port / path / scheme FIELD ON THE PROBE REQUEST
* (V-2b / A1).** `ProbeAppFixtureRequestSchema` is `.strict()` and carries exactly
* `{ namespace, appId, fixtureId }`. The target is derived on the DP from the
* fixture's own registered Service and its rendered readiness probe. A probe tool
* that accepts a caller-supplied target is an internal port scanner; this schema
* is the fence, and the DP handler having no code path that reads a target from
* the request is the second one.
*
* ## Conventions this file obeys (§3.0)
*
* - **Wire vocabulary is camelCase.** JetStream answers in snake_case (`first_seq`,
*   `ack_floor`); the DP maps once, at its adapter, and the `nats` package's own
*   `StreamInfo`/`ConsumerInfo` types **never** appear in this package (it has
*   exactly two runtime deps — `zod` and `@zion/sdk-contracts` — and `nats` is not
*   one of them).
* - **`.max()` is declared BEFORE `.regex()`** on every string, so input length is
*   bounded before the match runs (the repo's ReDoS rule;
*   `internal-bootstrap.schemas.ts` does the same).
* - **Requests are `.strict()`. Responses are NOT.** Same split as
*   `app-events-proxy.client.ts`: on the request an unknown key is a caller's
*   mistake and a 400 is right; on the response an unknown key is deploy skew
*   between a DP and a CP on different versions, and hard-failing every read on it
*   is worse than ignoring it.
* - Relative imports carry the **`.js` suffix** (this package's convention).
*/
/** A DNS-1123 label — k8s namespace / Service / SA names. Bounded before matched. */
const DNS_1123_LABEL = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
/**
* A DNS-1123 *subdomain* — k8s POD names. Dot-separated labels, max 253.
* Linear-time: every alternative is anchored by a literal `.`, so the outer `*`
* cannot backtrack ambiguously. Byte-identical to the one
* `internal-bootstrap.schemas.ts` already uses for `GetPodLogParamsSchema`.
*/
const DNS_1123_SUBDOMAIN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/;
const K8sNameSchema = z.string().min(1).max(63).regex(DNS_1123_LABEL);
/**
* ⛔⛔ **THE `appId` BOUND IS 253, NOT `MAX_APP_ID_LENGTH` (30) — AND HEAD DOCUMENTS
* THIS MISTAKE AT LENGTH (sec H-6).**
*
* The value is the app **SLUG**. `app-scope.ts:73-87` records the measured reason,
* having already rejected the obvious version once:
*
* > *"The obvious-looking version … also checks `MAX_APP_ID_LENGTH` (30). **That is
* > wrong and it would 500 on a working app.** … `createAppSchema` permits a slug up
* > to 64 chars. So a 40-char slug deploys fine, produces real pods"*
*
* — and there is **no DB CHECK on `release.apps.slug`**. The first draft of this
* file reproduced the rejected version at all five new doors, so a long-slug app's
* ENTIRE introspection surface would 400: every tool, every time, for an app that
* is running perfectly. ⇒ bound at the pod-name ceiling and gate on the CHARSET,
* exactly as `assertDeployableSlug` does.
*
* ⚠️ `MAX_FIXTURE_ID_LENGTH` **is** kept for `fixtureId` — the operator genuinely
* enforces that one on the deployment path, so a longer fixture id has no pods to
* introspect. The asymmetry is deliberate; do not "tidy" the two into one bound.
*
* ⛔ **Both are `export const`** so §5.3's middle-hop DTOs COMPOSE them rather than
* re-declaring the pattern (arch B-3). `ID_PART_PATTERN` is package-internal to
* `instance-identity.ts` and is NOT exported from this package's `index.ts` — so a
* developer told to "reuse the shared pattern" would have had to either invent an
* export or write a local regex, and the local regex is the exact divergence the
* import comment below forbids. Exporting the SCHEMAS, not the pattern, means the
* charset and the bound cannot diverge between the hops either.
*/
const AppIdSchema = z.string().min(1).max(253).regex(ID_PART_PATTERN);
const FixtureIdSchema = z.string().min(1).max(MAX_FIXTURE_ID_LENGTH).regex(ID_PART_PATTERN);
/**
* ⛔ **THE ONE UNREAD VOCABULARY (C-4 + spine V-3's discriminated-union shape,
* applied per row rather than per section).**
*
* Every read on this surface can fail independently, and each failure MUST be
* expressible as itself. An empty array standing in for "nothing was read" is the
* D1 trap this whole programme exists to delete: a NATS outage and an app with no
* messages are different answers, and an operator taught to read them as the same
* thing stops looking.
*
* The set is TOTAL and closed. Producer and consumer share it verbatim (D-5
* vocabulary equality), so a DP that learns a new failure mode must widen this
* enum — it cannot invent a string the CP will render as `undefined`.
*
*  - `nats_unavailable`      — core-api's single NATS connection is not up. ⚠️ This
*                              is the one that CANNOT be inferred from a null
*                              stream: `DisabledDlqNatsClient.describeStream()`
*                              returns `null`, byte-identical to "no such stream".
*                              See `isReady()` in §4.1.
*  - `jetstream_error`       — the broker answered, and it was not a 404.
*  - `stream_absent`         — the broker answered 404: the principal is enrolled
*                              but its stream has not been authored yet (the
*                              operator/M3 backstop's normal pre-converged state).
*  - `principal_not_enrolled`— the app has a Deployment with this ServiceAccount,
*                              but no ACTIVE `machine_principals` row binds it. A
*                              real finding, not an error.
*  - `kube_unavailable`      — the apiserver read failed (transport / 5xx).
*  - `kube_forbidden`        — the apiserver refused (RBAC drift — e.g. the
*                              `zion-observer` RoleBinding missing, the 2026-06-16
*                              incident shape).
*  - `no_health_path_declared`
*                            — ⭐ **the probe's refusal, named by ruling R-15.** The
*                              fixture has no registered Service, or its rendered
*                              Deployment declares no `readinessProbe.httpGet.path`.
*                              ⛔ NOT an error, and ⛔ never a guessed `/healthz`:
*                              a guessed path returns a healthy 404 and reports a
*                              working app as broken, which is worse than no answer.
*  - `not_declared`          — the general "there is nothing here to read" arm, used
*                              by `get_app_config` for a Deployment carrying no
*                              readable container. ⛔ Kept DISTINCT from
*                              `no_health_path_declared`: R-15 names that value for
*                              the probe specifically, and one string covering two
*                              unrelated absences is how a reason column stops being
*                              diagnosable. Collapsing either into `kube_unavailable`
*                              would send an operator to look at a healthy cluster.
*  - `dp_endpoint_unavailable`
*                            — ⭐ **the FORWARD-COMPAT arm (R-37.5 / devops B4), and
*                              it exists because V-11 guarantees the window.** The
*                              release train is CP-first-then-DP under one lock, so
*                              between the two stages every DP is running a core-api
*                              that has never heard of these routes and answers
*                              **404**. Untranslated, that 404 becomes a
*                              `DpHttpError` ⇒ a 500 at provisioning-svc ⇒ a
*                              stripped **502** at site-admin-api, which the app
*                              owner reads as *"the platform is broken."* It is not;
*                              their data plane is simply a version behind, and the
*                              remedy is an upgrade, not a page. ⛔ This is the ONE
*                              404 that is allowlisted for translation (§5.5);
*                              every other DP status keeps the stripped-502 default.
*
*                              ⚠️ **Only the CP can emit this one, and it lives here
*                              anyway — deliberately (R-66.5).** This enum is the
*                              shared vocabulary of *reasons a section is unread*,
*                              and its audience is the AGENT reading the response,
*                              not the plane that authored it. An agent should not
*                              have to learn two reason vocabularies depending on
*                              which hop failed. The DP producer simply never
*                              produces this member; its own-output `.parse` would
*                              accept it, which costs nothing.
*
*  ⚠️ **The two Postgres-shaped values below this docblock carry their documentation INLINE on
*  the value (R-001), not as bullets here.** One value, one documentation home. A bullet plus an
*  inline block is two texts for one member, and they drift; nine bullets over eleven members is
*  a "the set is TOTAL" sentence that has stopped being true. Read `db_unavailable` and
*  `no_samples_yet` at their declarations.
*/
const AppIntrospectionUnreadReasonSchema = z.enum([
	"nats_unavailable",
	"jetstream_error",
	"stream_absent",
	"principal_not_enrolled",
	"kube_unavailable",
	"kube_forbidden",
	"no_health_path_declared",
	"not_declared",
	"dp_endpoint_unavailable",
	"db_unavailable",
	"no_samples_yet"
]);
/**
* The read/unread discriminated union, parameterised by payload.
*
* ⛔ A function returning `z.discriminatedUnion` rather than a
* `{read:boolean, data?:T, reason?:R}` object: the loose form admits
* `{read:false, data:{...}}` and `{read:true}` with no data, both of which are
* lies the type system should not be able to express. The `read` literal is the
* discriminant, so exactly one arm carries `data` and exactly one carries
* `reason`.
*/
function sectionSchema(payload) {
	return z.discriminatedUnion("read", [z.object({
		read: z.literal(true),
		data: payload
	}), z.object({
		read: z.literal(false),
		reason: AppIntrospectionUnreadReasonSchema
	})]);
}
/**
* ⛔ `.strict()`, and the absent keys matter as much as the present ones. There is
* no `streamName`, no `principalId`, no `subject`, no `labelSelector` — every one
* of those would let a caller aim the read somewhere the app does not own. The DP
* derives the entire principal set from `appId` (§4.3) and nothing else.
*
* ⚠️ `namespace` IS caller-supplied, exactly as on the four shipped
* `/internal/observability/*` verbs, and it is NOT an app-scope hole: it is
* resolved by provisioning-svc from `:dpId` (`resolveDpNamespace`), never by the
* app owner, and a DP's core-api can only see its own cluster anyway.
*/
const AppStreamInfoQuerySchema = z.object({
	namespace: K8sNameSchema,
	appId: AppIdSchema
}).strict();
/** Retention policy vocabulary — the three JetStream policies, mapped from the broker's own strings. */
const StreamRetentionSchema = z.enum([
	"limits",
	"interest",
	"workqueue"
]);
/**
* The per-stream state, mapped from JetStream's `StreamInfo`.
*
* ⚠️ `firstTs`/`lastTs` are `.nullable()` and NOT defaulted to `''` or to "now".
* An empty stream has no first/last message timestamp, and the broker sends the
* zero instant; the mapper turns that into `null`. The `?? ''` conflation is the
* one `mapRestEventToLegacy` makes and that E4 refused to inherit.
*
* ⚠️ `maxAgeSeconds` is nullable because JetStream expresses "unlimited" as `0`
* nanos. `0` on the wire would read as "expires immediately", which is the exact
* opposite. The mapper emits `null` for unlimited.
*/
const AppStreamStateSchema = z.object({
	streamName: z.string().min(1).max(255),
	messages: z.number().int().nonnegative(),
	bytes: z.number().int().nonnegative(),
	firstSeq: z.number().int().nonnegative(),
	firstTs: z.string().nullable(),
	lastSeq: z.number().int().nonnegative(),
	lastTs: z.string().nullable(),
	numDeleted: z.number().int().nonnegative(),
	consumerCount: z.number().int().nonnegative(),
	retention: StreamRetentionSchema,
	maxAgeSeconds: z.number().int().nonnegative().nullable(),
	subjects: z.array(z.string().min(1)).readonly()
});
/**
* One consumer's position on the stream — the "which consumer is behind" answer
* the field report spent a night without.
*
* ⛔ **Counts and sequences ONLY. No message payloads, no headers, no subjects
* beyond the consumer's own `filterSubject`** (security-audit A3: *"Counts/seqs
* only — never message payloads"*). There is deliberately no field on this object
* that could carry tenant data.
*/
const AppStreamConsumerSchema = z.object({
	name: z.string().min(1).max(255),
	/** The highest contiguously acked STREAM sequence — the "ack floor" the report asked for. */
	ackFloorStreamSeq: z.number().int().nonnegative(),
	ackFloorConsumerSeq: z.number().int().nonnegative(),
	deliveredStreamSeq: z.number().int().nonnegative(),
	numAckPending: z.number().int().nonnegative(),
	numRedelivered: z.number().int().nonnegative(),
	numPending: z.number().int().nonnegative(),
	numWaiting: z.number().int().nonnegative(),
	ackPolicy: z.enum([
		"none",
		"all",
		"explicit"
	]),
	/** ISO-8601, or `null` when the consumer has never delivered. */
	lastActiveAt: z.string().nullable(),
	/** `null` when the consumer filters nothing (the whole stream). */
	filterSubject: z.string().min(1).nullable()
});
/**
* One of the app's principals, as this endpoint sees it.
*
* ⭐ The row is keyed by `{fixtureId, serviceAccountName}` — which the DP knows
* from the app's own Deployments — and the principal is a NESTED union, because
* "this fixture's SA is not enrolled" is a first-class answer, not an error. That
* is precisely the state `get_app_dependencies` exists to surface, and rendering
* it as an absent row would hide it.
*/
const AppStreamPrincipalSchema = z.object({
	fixtureId: FixtureIdSchema,
	serviceAccountName: K8sNameSchema,
	principal: z.discriminatedUnion("enrolled", [z.object({
		enrolled: z.literal(true),
		principalId: z.string().min(1).max(64),
		/** The stream slug — `machine_principals.agent_id`, or the row id for legacy rows. */
		agentId: z.string().min(1).max(64),
		/** `S_<orgId>_<agentId>`, derived server-side by `agentStreamName`. */
		streamName: z.string().min(1).max(255)
	}), z.object({ enrolled: z.literal(false) })]),
	stream: sectionSchema(AppStreamStateSchema),
	consumers: sectionSchema(z.array(AppStreamConsumerSchema).readonly())
});
/**
* ⚠️ **§V-1 EXEMPT — bounded by the app's declared fixture set** (spine §V-1's
* `get_app_rollout_diagnostics` exemption, same rationale and same remedy). The
* cardinality is the number of brokered service fixtures the app declares, which
* is operator-scale; a cursor into it would be ceremony. `truncated` + a server
* cap is the future-proofing the spine's own exemption text prescribes, so the
* silent-cap tier stays empty.
*/
const AppStreamInfoResponseSchema = z.object({
	appId: AppIdSchema,
	namespace: K8sNameSchema,
	fetchedAt: z.string().min(1),
	principals: z.array(AppStreamPrincipalSchema).readonly(),
	truncated: z.boolean()
});
/** Server cap on `principals`. Hitting it sets `truncated` — never a silent cap. */
const MAX_APP_STREAM_PRINCIPALS = 200;
const AppDependenciesQuerySchema = z.object({
	namespace: K8sNameSchema,
	appId: AppIdSchema
}).strict();
/**
* What KIND of dependency a row is about. Closed vocabulary, shared both ways.
*
*  - `own_stream`      — the principal's own `S_<org>_<agentId>` JetStream stream.
*  - `sibling_stream`  — a stream this principal DECLARES it consumes
*                        (`machine_principals.consumes_stream_principals`).
*  - `secret`          — a k8s Secret the fixture's rendered Deployment references
*                        (`envFrom.secretRef` / `volumes[].secret`).
*  - `principal`       — the `machine_principals` row itself, bound by `k8s_subject`.
*/
const AppDependencyKindSchema = z.enum([
	"own_stream",
	"sibling_stream",
	"secret",
	"principal"
]);
/**
* ⭐ The spine's shape verbatim: *"each row `{expected, actual, ok}`"*.
*
* ⛔ **`actual` is a UNION, not a nullable string, and `ok` is not a synonym for
* `actual !== null`.** A dependency whose actual state COULD NOT BE READ is not
* "missing" — it is unknown, and `ok:false` on an unread row would report an
* outage as a misconfiguration. So `actual` is `sectionSchema(...)` and `ok` is
* itself `boolean | null`: `null` ⇔ `actual.read === false`. A consumer rendering
* a red cross for an unread row is then making a visible mistake rather than
* repeating the producer's.
*/
const AppDependencyRowSchema = z.object({
	kind: AppDependencyKindSchema,
	/** What the desired state says must exist. Server-derived; never caller-supplied. */
	expected: z.string().min(1).max(255),
	/** What is actually there, or why we could not tell. */
	actual: sectionSchema(z.object({
		present: z.boolean(),
		/** Free-form, BOUNDED detail — e.g. `messages=0`, `seq=1..842`. Never a payload. */
		detail: z.string().max(200).nullable()
	})),
	/** `true`/`false` only when `actual.read`; `null` otherwise. See the docblock. */
	ok: z.boolean().nullable()
});
const AppDependenciesPrincipalSchema = z.object({
	fixtureId: FixtureIdSchema,
	serviceAccountName: K8sNameSchema,
	rows: z.array(AppDependencyRowSchema).readonly()
});
const AppDependenciesResponseSchema = z.object({
	appId: AppIdSchema,
	namespace: K8sNameSchema,
	fetchedAt: z.string().min(1),
	principals: z.array(AppDependenciesPrincipalSchema).readonly(),
	truncated: z.boolean()
});
/** Server cap on `principals`, and on `rows` within one principal. */
const MAX_APP_DEPENDENCY_PRINCIPALS = 200;
const MAX_APP_DEPENDENCY_ROWS_PER_PRINCIPAL = 100;
const AppConfigQuerySchema = z.object({
	namespace: K8sNameSchema,
	appId: AppIdSchema,
	/** Narrow to one fixture. Absent ⇒ every fixture of the app. */
	fixtureId: FixtureIdSchema.optional()
}).strict();
/**
* Where a container env entry gets its value FROM. This is the discriminator that
* makes `valueHmac: null` honest rather than lossy: a `secretRef` entry has a
* value, we simply refuse to read it, and saying so is different from saying the
* variable is unset.
*
*  - `literal`       — `env[].value` is a plain string in the pod spec. HMAC'd.
*  - `secretRef`     — `env[].valueFrom.secretKeyRef`. ⛔ NOT resolved (E-3b/A2).
*  - `configMapRef`  — `env[].valueFrom.configMapKeyRef`. Not resolved either, for
*                      symmetry: a ConfigMap is not a secret, but reading one to
*                      hash it would add an apiserver read per key for a
*                      comparator the `ref` already provides.
*  - `fieldRef`      — `env[].valueFrom.fieldRef` / `resourceFieldRef`. The value is
*                      pod-instance-dependent, so a hash of it is not a comparator.
*  - `envFrom`       — the variable is projected wholesale from a Secret/ConfigMap
*                      via `envFrom`, so the pod spec does not name it at all. See
*                      `AppConfigSourceRefSchema`'s note.
*/
/**
* ⚠️ **`'envFrom'` is a FIFTH value beyond ruling R-14's four, kept deliberately and
* flagged to the lead rather than added quietly.**
*
* R-14 enumerates `literal | secretRef | configMapRef | fieldRef`. But the operator
* injects a fixture's svc-NATS credentials and its DB credentials via
* `envFrom.secretRef` (`zion-operator/src/reconciler/env-from.ts:96`), and an
* `envFrom` projection names **no individual variables in the pod spec at all**.
* With only R-14's four values, `get_app_config` would therefore report *nothing*
* for the most operationally important secrets a fixture runs with — silence that
* an operator would read as "this fixture has no credentials configured". One row
* per projected object (`name` = the object name, `key: null`) is the honest
* alternative; inventing variable names would be worse than both.
*/
const AppConfigSourceSchema = z.enum([
	"literal",
	"secretRef",
	"configMapRef",
	"fieldRef",
	"envFrom"
]);
/**
* The reference behind a non-literal entry — object NAME and KEY only. Shape per
* ruling **R-14**: `ref?: {name, key}`, OPTIONAL and absent on a `literal`.
*
* ⭐ This is the comparator for a secret, and it needs no plaintext: *"is fixture A
* pointed at the same Secret and key as fixture B"* and *"did this fixture's
* secret reference change"* are both answerable from the reference alone. That is
* why option A (never read Secrets) loses nothing that matters — the only question
* it cannot answer is "did the bytes inside rotate", and buying that answer costs
* materialising every tenant secret into a hashing path on a CP-triggered read.
*
* ⚠️ `key` stays `.nullable()` INSIDE an otherwise-present `ref`, because the two
* absences are different: a `literal` has no reference at all (⇒ `ref` omitted),
* while an `envFrom` projection HAS a reference and genuinely has no key (⇒ `ref`
* present, `key: null`). Collapsing them would make "no reference" and "whole
* object projected" indistinguishable.
*/
const AppConfigSourceRefSchema = z.object({
	/** k8s object name (Secret / ConfigMap). */
	name: z.string().min(1).max(253),
	/** The key within it, the field path for `fieldRef`, or `null` for `envFrom`. */
	key: z.string().min(1).max(253).nullable()
});
/**
* ⛔⛔ **THE V-2a ROW. THERE IS NO `value`, AND THERE NEVER WILL BE.**
*
* `valueHmac` is **HMAC-SHA256 under a DP-LOCAL key**, hex, 64 chars — ⛔ NOT a
* bare SHA-256. Env values are low-entropy (`true`, `production`, a port number, a
* URL, a tenant slug), so a bare digest is a dictionary lookup away from the
* plaintext and would be redaction in name only. The keyed MAC makes the digest
* useless to anyone without the DP's key while preserving the only property the
* tool needs: equality.
*
* `valueHmac` is `null` for every non-`literal` source — see `AppConfigSourceSchema`.
* `valueLength` is likewise `null` there: a length we did not measure must not be
* reported as `0`.
*
* ⚠️ **`valueLength` is a deliberate, bounded disclosure and is worth stating.** It
* leaks the character count of a literal env value. Literals are the entries that
* are NOT secrets by construction (a secret arrives by `secretRef`/`envFrom`), and
* length is what makes the comparator diagnosable — "the HMAC changed and the
* length went 12 → 0" is an operator's answer; two opaque digests are not.
*/
const AppConfigEntrySchema = z.object({
	name: z.string().min(1).max(253),
	source: AppConfigSourceSchema,
	/** ⛔ OPTIONAL per R-14 — absent on a `literal`, present on every other source. */
	ref: AppConfigSourceRefSchema.optional(),
	/** hex HMAC-SHA256, or `null` when the value was deliberately not read. */
	valueHmac: z.string().length(64).regex(/^[0-9a-f]+$/).nullable(),
	valueLength: z.number().int().nonnegative().nullable()
});
const AppConfigFixtureSchema = z.object({
	fixtureId: FixtureIdSchema,
	/** The rendered Deployment the entries were read from — the provenance of the answer. */
	deploymentName: z.string().min(1).max(253),
	containerName: K8sNameSchema,
	entries: sectionSchema(z.array(AppConfigEntrySchema).readonly())
});
/**
* ⭐ `hmacKeyId` — WS3-I9, and it is load-bearing rather than decorative.
*
* The HMAC key is derived from a DP secret (§4.6). When that secret rotates, every
* `valueHmac` changes even though no config changed — so a consumer comparing two
* responses across a rotation would confidently report a change that did not
* happen. `hmacKeyId` is a fingerprint OF THE KEY (`HMAC(key, kid-label)`,
* first 8 hex), which discloses nothing about the key and lets a consumer refuse
* to compare across a boundary. Without it the comparator is quietly unsound.
*/
const AppConfigResponseSchema = z.object({
	appId: AppIdSchema,
	namespace: K8sNameSchema,
	fetchedAt: z.string().min(1),
	hmacKeyId: z.string().length(8).regex(/^[0-9a-f]+$/),
	fixtures: z.array(AppConfigFixtureSchema).readonly(),
	truncated: z.boolean()
});
const MAX_APP_CONFIG_FIXTURES = 100;
const MAX_APP_CONFIG_ENTRIES_PER_FIXTURE = 300;
/**
* ⛔⛔ **THE SSRF FENCE (V-2b / A1). READ BEFORE ADDING A FIELD.**
*
* These three keys are the WHOLE request, and `.strict()` makes any fourth a 400.
* ⛔ Never add `url`, `host`, `hostname`, `port`, `path`, `scheme`, `protocol`,
* `target`, `endpoint`, `address`, `follow`, `redirects`, `timeoutMs`, `method`,
* or `headers`. Each one converts a fixture health check into a
* caller-steerable in-cluster request — an internal port scanner reachable by any
* principal holding `cp:app:runtime:read` on ONE app.
*
* The target is derived on the DP from the fixture's own registered Service and
* its rendered readiness probe (§4.7). Redirects are not followed. The response
* body is not returned. The timeout is server-fixed. Those four properties are
* enforced in the handler, not here — this schema is the first fence, not the
* only one.
*/
const ProbeAppFixtureRequestSchema = z.object({
	namespace: K8sNameSchema,
	appId: AppIdSchema,
	fixtureId: FixtureIdSchema
}).strict();
/**
* The transport-failure taxonomy the spine names (dns / connect / tls / timeout).
*
* ⚠️ **`http-status` is NOT in this enum, deliberately.** An HTTP 500 is a
* RESPONSE — the probe worked and the app answered badly — so it lands in the
* `outcome:'response'` arm with its status. Folding it in here would make
* "the app is broken" and "the network is broken" the same answer, which is the
* exact conflation the taxonomy exists to prevent.
*/
const ProbeErrorSchema = z.enum([
	"dns",
	"connect",
	"tls",
	"timeout"
]);
/**
* ⛔ **NO BODY, EVER.** `responseBytes` is a COUNT. There is no field on this
* object that can carry a byte the app emitted, and the handler discards the body
* without decoding it. A health endpoint routinely echoes build info, env names,
* connection strings and dependency hosts; returning it would turn a probe into an
* exfiltration primitive with a friendlier name.
*
* ⚠️ A 3xx lands here with its status and `redirected: false`. Redirects are NOT
* followed — following one is how a probe pointed at a fixture ends up talking to
* the metadata service.
*/
const ProbeAppFixtureOutcomeSchema = z.discriminatedUnion("outcome", [z.object({
	outcome: z.literal("response"),
	status: z.number().int().min(100).max(599),
	latencyMs: z.number().int().nonnegative(),
	responseBytes: z.number().int().nonnegative(),
	redirected: z.literal(false)
}), z.object({
	outcome: z.literal("error"),
	error: ProbeErrorSchema,
	latencyMs: z.number().int().nonnegative()
})]);
/**
* The probe target, ECHOED so the caller can see what the server chose.
*
* ⭐ Echoing it is not a leak and it is not optional: the caller could not steer
* the probe, so it has no other way to know whether the DP probed the endpoint it
* meant. Every component here is server-derived from objects the caller is already
* authorized to read through `get_app_pods`. ⛔ It is echoed on the RESPONSE only —
* nothing on the request may name it.
*/
const ProbeTargetSchema = z.object({
	serviceName: z.string().min(1).max(253),
	port: z.number().int().min(1).max(65535),
	path: z.string().min(1).max(2048),
	scheme: z.literal("http"),
	/**
	* ⭐ WHICH version's declared health path was probed, taken from the stable
	* Service's own `zion.witness.ai/version` selector (sec B-7). On a multi-version
	* fixture every version has its own Deployment with the same fixture label, so
	* without this an operator cannot tell whether the path shown is the one they
	* just shipped or a lingering predecessor's.
	*/
	versionSlug: z.string().max(16).regex(VERSION_SLUG_PATTERN)
});
const ProbeAppFixtureResponseSchema = z.object({
	appId: AppIdSchema,
	fixtureId: FixtureIdSchema,
	namespace: K8sNameSchema,
	probedAt: z.string().min(1),
	/** The server-fixed budget, echoed so the caller can interpret a `timeout`. */
	timeoutMs: z.number().int().positive(),
	/**
	* ⚠️ `sectionSchema` again: "this fixture declares no health path" is
	* `{read:false, reason:'no_health_path_declared'}`, NOT a fabricated probe of `/healthz`
	* and NOT an `outcome:'error'`. Guessing a path is how a probe reports a healthy
	* 404 as a broken app.
	*/
	result: sectionSchema(z.object({
		target: ProbeTargetSchema,
		outcome: ProbeAppFixtureOutcomeSchema
	}))
});
/** ⛔ SERVER-FIXED. Not configurable, not caller-supplied, not an env var (V-2b). */
const PROBE_TIMEOUT_MS = 3e3;
/** Bound on the body we count before abandoning it. The body itself is never kept. */
const PROBE_MAX_RESPONSE_BYTES = 65536;
/**
* ⛔ **STRUCTURED PARAMS ONLY. NO RAW LogQL CROSSES THIS ROUTE (R-1 item 1).**
*
* The shipped sibling `GET :id/loki/query` forwards caller-authored LogQL verbatim
* — acceptable there because the CP authored it. This route is reached by an MCP
* TOOL argument, so LogQL injection (in the repo's own fix catalog) becomes
* reachable by an agent. ⛔ There is no `query`, `logql`, `expr` or `selector`
* field, and `.strict()` 400s any of them. The DP composes the LogQL with the
* shared builder (§12.1).
*/
/**
* ⛔⛔ **THE HIERARCHY RULE, ONE COPY, APPLIED AT BOTH DOORS.**
*
* The pod-name scope is hierarchical (`<slug>--<fixtureId>--<versionSlug>-…`) and
* a level cannot be skipped. The CP's shipped `appScopePodPrefix` THROWS 400 for
* `versionSlug` without `fixtureId`; the DP's composer cannot throw — it is a pure
* string builder — and its failure mode for that shape is not an exception but
* `{k8s_pod_name=~"<slug>--.*"}`: **the whole app, counted for a caller who asked
* for one version.** A count is a single number with no evidence attached, so
* nothing downstream can notice.
*
* ⇒ Exported as a PREDICATE, not re-typed at each door, because provisioning-svc's
* middle hop needs the identical rule on a differently-named field set (`slug`
* rather than `appId`) and a second copy is how the two drift.
* `LOGQL_SELECTOR_GOLDEN_REJECTS` is the shared row set both sides assert against
* (GOLD-6).
*/
function versionSlugRequiresFixtureId(q) {
	return q.versionSlug === void 0 || q.fixtureId !== void 0;
}
/** The refusal message both doors emit, so an operator sees one sentence. */
const VERSION_SLUG_REQUIRES_FIXTURE_ID = "versionSlug requires fixtureId: the pod-name scope is hierarchical (<slug>--<fixtureId>--<versionSlug>-…) and a level cannot be skipped.";
/**
* ⚠️ **THE UNREFINED FIELD SET, EXPORTED SO THE MIDDLE HOP CAN DERIVE FROM IT.**
*
* `AppLogsAggregateQuerySchema` below is this object plus the hierarchy
* refinement, and zod refuses `.omit()`/`.extend()` on a schema carrying
* refinements — so a middle-hop DTO that wants the same bounds under a different
* `appId`/`slug` spelling has to start here. ⛔ That is the ONLY reason this is
* exported. Consumers validating a DP request use the refined schema below; using
* this one directly skips the hierarchy rule.
*/
const AppLogsAggregateQueryFieldsSchema = z.object({
	namespace: K8sNameSchema,
	appId: AppIdSchema,
	fixtureId: FixtureIdSchema.optional(),
	/**
	* ⛔⛔ **THE REGEX IS THE CONTROL, AND ITS ABSENCE WAS A LIVE LogQL INJECTION
	* ON THE ROUTE WHOSE DOCBLOCK OPENS "NO RAW LogQL CROSSES THIS ROUTE" (E-2).**
	*
	* `versionSlug` is interpolated into a **`=~` matcher**, where metacharacters
	* are EVALUATED — so a bare `.*` widens the scope with no quote-breaking at
	* all. Executed against this plan's own composer:
	*   `versionSlug: '.*'` ⇒ `{k8s_pod_name=~"alpha--api--.*-.*"}`
	* ⇒ every version of the fixture, from a caller who asked for one.
	*/
	versionSlug: z.string().max(16).regex(VERSION_SLUG_PATTERN).optional(),
	/**
	* ⛔ ADDED IN ROUND 1 (R-41.3). The first draft omitted `pod`/`container`
	* while WS-2 forwards them AND advertises them ("narrow the count exactly as
	* they narrow the lines"). Against a `.strict()` schema that meant a
	* `pod`-bearing aggregate either **400s at the DP** or — worse, had the schema
	* been loose — **silently counted the whole app**: the advertised-and-discarded
	* defect, made sharper because R-39.1 now has the CP confining exactly these
	* identifiers before the hop.
	*/
	/**
	* ⛔⛔ **GRAMMAR-BOUNDED (E-2). A length cap is not a control.** `pod` is
	* interpolated into an exact `=` matcher, and without the grammar a `"`
	* closes it and appends a selector term. Executed:
	*   `pod: 'x", k8s_namespace_name=~".*'`
	*     ⇒ `{k8s_pod_name="x", k8s_namespace_name=~".*"}`
	* — the LogQL-injection entry in this repo's own bot fix-catalog.
	*
	* ⛔ And the grammar is NOT subsumed by §12.3b's `startsWith(prefix)` check.
	* Executed: `pod: 'alpha--x", k8s_namespace_name=~".*'` **passes**
	* `startsWith('alpha--')` and still injects, because the payload sits AFTER a
	* valid prefix. That is exactly why the CP runs three ORDERED arms — length,
	* then grammar, then prefix — and why this plan must too.
	*
	* ⚠️ `.max()` before `.regex()` (ReDoS rule).
	*/
	pod: z.string().max(253).regex(DNS_1123_SUBDOMAIN).optional(),
	container: K8sNameSchema.optional(),
	/** ⛔ Allowlisted by the shared level enum — never free text. */
	level: z.string().min(1).max(16).optional(),
	/** Substring match; escaped by `escapeLogqlLiteral` before it reaches LogQL. */
	search: z.string().min(1).max(256).optional(),
	start: z.string().min(1).max(64),
	end: z.string().min(1).max(64),
	/**
	* ⛔ `mode`, NOT a `byLevel` boolean (R-41.2, arch B-4). Two reasons, either
	* fatal on its own: (a) a `z.boolean()` on a **GET query** schema is broken —
	* query values arrive as strings, so it needs `z.coerce`, and
	* `z.coerce.boolean()` maps the string `'false'` to `true` (every non-empty
	* string is truthy), which is a documented house rule with a pinning test;
	* (b) WS-2's tool arg is `aggregate: 'count' | 'count_by_level'`, so a boolean
	* here made the seam carry two vocabularies for one concept. ONE vocabulary,
	* tool arg to wire.
	*/
	mode: z.enum(["count", "count_by_level"])
}).strict();
/**
* The DP request schema — the field set above plus the hierarchy refinement
* (§12.3b). This is what the DP door binds and what a CP consumer validates
* against; ⛔ never `AppLogsAggregateQueryFieldsSchema` on its own.
*
* ⚠️ Refined AFTER `.strict()` on purpose: in this repo's zod a refinement on an
* object returns the object type with `.shape` intact and the unknown-key
* rejection preserved, so `ZionStrictValidationPipe`'s deep-strict still sees a
* shape to walk. Reversing the order is not equivalent and is not tested.
*/
const AppLogsAggregateQuerySchema = AppLogsAggregateQueryFieldsSchema.refine(versionSlugRequiresFixtureId, { message: VERSION_SLUG_REQUIRES_FIXTURE_ID });
/**
* R-1 item 4's shape.
*
* ⛔ **No `lines` field, and no `truncated` field.** V-4's whole point is that a
* count cannot drag payloads through an agent's context — so the field is
* structurally absent, not merely unset. And there is **no truncation concept**
* on an instant query (R-1 item 3): a `sum(count_over_time(...))` returns one
* vector sample or it errors. ⛔ Do not add `truncated: false` "for symmetry" — a
* permanently-false flag teaches a reader the value is meaningful.
*
* ⚠️ `byLevel` is `.optional()`, NOT defaulted to `{}`: absent means the caller did
* not ask for buckets (`mode: 'count'`); `{}` would mean "asked, and every level
* was zero". Two different facts.
*
* ⛔ **When present, `byLevel` carries the FULL normalized level domain with zeros
* included, keyed in lowercase** (R-41.4). Not observed-only, and not upper-case:
*   - **full domain** so an agent can distinguish *"zero errors"* from *"`error`
*     is not a level this surface knows"* — an observed-only map makes those the
*     same absence, which is the C-4 shape in miniature;
*   - **normalized lowercase** because that is the vocabulary `normalizeLevel`
*     produces and the one WS-2's consumer asserts. The first draft emitted
*     UPPERCASE observed-only and WS-2 asserted lowercase observed-only — both
*     halves wrong, and green against both sides' fakes (R-41).
*
* ⛔⛔ **PLUS THE `other` RESIDUAL, SO `Σ(byLevel) === total` IS AN IDENTITY.**
* `detected_level` is Loki's own label and its value domain is NOT bounded by
* `ALLOWED_LEVELS`, so a line at a level outside the seven counts in `total` and
* belongs in no named bucket. Without `other` it is silently lost: the breakdown
* disagrees with the number printed beside it and nothing says why — on a surface
* whose whole purpose is *"what is wrong with my app right now"*. The producer
* computes it as `total − Σ(seven)` from the unfiltered query it already ran, so
* there is no extra query. ⛔ `other` is deliberately NOT a member of
* `NORMALIZED_LOG_LEVELS`, so it cannot shadow a real bucket. ⇒ **`byLevel` has
* eight keys when present**, and `Σ(byLevel) === total` is a CHECKABLE invariant
* rather than the `≤` no arm can meaningfully pin.
*
* ⛔ **`window` is the DP's echo and PASSES THROUGH to the tool response** — WS-2
* no longer re-mints it (R-41.6). Two mintings of one window is how a response
* claims a range it did not count.
*/
const AppLogsAggregateResponseSchema = z.object({
	total: z.number().int().nonnegative(),
	byLevel: z.record(z.string(), z.number().int().nonnegative()).optional(),
	/**
	* ⛔⛔ **`false` ⇔ the selector matched NO LOG STREAMS AT ALL — R-39.2.**
	*
	* ⭐ **This one bit is the difference between a count and a lie.** Without it
	* `{total: 0}` is the only thing a broken collector can say, and `total: 0` reads
	* as *"your app was quiet for an hour"* — verbatim the sentence `get_app_status`
	* was DELETED for rendering. The producer can already tell the two apart at no
	* extra cost: an instant `sum(count_over_time(...))` returns an **empty vector**
	* when no stream matches the selector, and a vector carrying a value when
	* streams matched and counted N lines — **including N = 0**.
	*
	* ⛔ **It comes from the FIRST, UNFILTERED query ONLY.** Under `count_by_level`
	* there are eight queries, and a per-level query legitimately returns an empty
	* vector for a level with no lines — so `&&`-folding them would report
	* `matchedStreams: false` for a healthy app that merely logged no `trace`. That
	* is the same lie arriving from the other direction, inside the field added to
	* prevent it.
	*
	* ⛔ **REQUIRED. Never `.optional()`, never `.default()`, and never defaulted on
	* the consumer side.** A defaulted `true` is indistinguishable from a data plane
	* that never learned to send the field, which is precisely the failure this bit
	* exists to make impossible.
	*
	* ⚠️⚠️ **DECLARED HERE BEFORE ANY PRODUCER POPULATES IT, AND THE ORDER IS THE
	* RULING (R-93 F-2), because the reverse fails SILENTLY.** This schema is a bare
	* `z.object` — non-strict — so `.parse()` **drops an undeclared key without
	* throwing**. Measured on this very field, from `site-admin-api`:
	*
	* ```
	*   EMITTED keys : matchedStreams,total,window
	*   PARSED  keys : total,window          ← stripped
	*   threw?       : no
	*   control      : `total` (declared) SURVIVED — so the probe was live
	* ```
	*
	* ⇒ Populate before declaring and the producer's own self-parse deletes the
	* field: **the unit test passes, the contract spec passes, the consumer's
	* fake-based tests pass, and the field is absent in production.** ⛔ Never
	* "populate now, declare later."
	*/
	matchedStreams: z.boolean(),
	window: z.object({
		start: z.string().min(1),
		end: z.string().min(1)
	})
});
/**
* ⛔ `.strict()`, and the absent keys matter as much as the present ones. There is
* no `schemaName`, no `tableName`, no `fromIso`, no `toIso` and no `limit` — every
* one of those would let a caller aim the read somewhere the app does not own, or
* widen the window past what the store holds. The DP resolves the app's schema set
* from `appId` through R-006's ownership predicate and nothing else, and the
* window is server-fixed.
*
* ⚠️ `namespace` IS caller-supplied, exactly as on the four shipped verbs, and the
* controller asserts it equals the pod's own namespace (R-37.4).
*/
const AppDatabaseObservationQuerySchema = z.object({
	namespace: K8sNameSchema,
	appId: AppIdSchema
}).strict();
/**
* One table's observation over the window.
*
* Every `*Delta` field sums the per-interval deltas across the window's buckets. So it counts
* events inside the window, and it is never a cumulative total since the statistics were reset.
* `liveTuples` and `deadTuples` are the last observed values, because a tuple count is a level and
* not a rate.
*/
const AppDatabaseTableObservationSchema = z.object({
	/**
	* The Postgres schema. A fixture's schema name is whatever `resolveFixtureSchemaName` resolved
	* for it: a declared `infrastructure.database.ownSchema` wins, and absent one the derivation is
	* `<appSlug>_<fixtureId>` with hyphens replaced by underscores. It is NOT the fixture's role
	* name, which is a different rule.
	*/
	schemaName: z.string().min(1).max(63),
	tableName: z.string().min(1).max(63),
	/**
	* The app the DP attributed this row to, resolved through R-006's ownership predicate. The CP
	* asserts this equals the app it requested and refuses the whole response otherwise (R-010,
	* control C-3). A row whose value differs from the request is a filter defect in the DP, and
	* this field is what makes that defect loud.
	*/
	attributedAppKey: AppIdSchema,
	/**
	* The fixture this schema belongs to. Always present: under R-006 and R-026 a schema is
	* attributed only through the fixture role that owns it, so every attributed schema has a
	* fixture owner, and `app_db_table_stat_buckets.fixture_id` is `NOT NULL` (R-038). R-006's
	* predicate CARRIES this forward from the role that owns the schema. It is never recovered from
	* the schema name, which a declared `ownSchema` makes unparseable, and never derived from the
	* name, which `deriveFixtureSchemaName` is not injective enough to support across apps.
	*/
	fixtureId: FixtureIdSchema,
	/** Sequential scans started in the window. */
	seqScanDelta: z.number().int().nonnegative(),
	/** Live rows returned by those sequential scans in the window. */
	seqTupReadDelta: z.number().int().nonnegative(),
	/** Index scans started in the window. Reported for contrast, so a reader can see a table that is scanned and never indexed. */
	idxScanDelta: z.number().int().nonnegative(),
	/** Last observed live tuple estimate. A level, not a rate. */
	liveTuples: z.number().int().nonnegative(),
	/** Last observed dead tuple estimate. A level, not a rate. */
	deadTuples: z.number().int().nonnegative(),
	/** Heap blocks read from disk in the window. */
	heapBlksReadDelta: z.number().int().nonnegative(),
	/** Heap blocks served from cache in the window. */
	heapBlksHitDelta: z.number().int().nonnegative(),
	/**
	* How many five-minute buckets this relation has a row in. Lower than the window's capacity
	* means the relation was idle in the rest. `positive()`: a relation row exists only because at
	* least one bucket held a non-zero delta or a reset-dropped interval, so a read row always has
	* one contributing bucket.
	*/
	bucketsContributing: z.number().int().positive(),
	/**
	* Intervals dropped because a counter decreased, which means the statistics were reset by
	* `pg_stat_reset()`, a failover or an instance restart. Surfaced rather than hidden: a
	* non-zero value tells a reader the window undercounts, and by how many intervals.
	*/
	resetSkippedBuckets: z.number().int().nonnegative(),
	/**
	* Per-bucket `seq_scan` deltas, oldest first, `window.bucketCount` entries. Index `i` is the
	* bucket starting at `window.fromIso` plus `i * window.bucketSeconds`; the series carries no
	* timestamps of its own. `null` where the sampler held no bucket at that start (no row in the
	* aggregate table), `0` where the sampler ran and this relation did not move. The reader
	* locates an onset from this. The platform computes no threshold and no onset (R-019, R-021,
	* R-027, D9).
	*/
	seqScanSeries: z.array(z.number().int().nonnegative().nullable()).readonly()
});
const AppDatabaseAggregateSchema = z.object({
	/**
	* ⚠ This is the whole data plane's database, not this app's share. `pg_stat_database` counts
	* per database, and every app on a data plane shares one. `scope` carries that fact onto the
	* wire so no reader has to know it, and so no consumer can present it as the app's own number.
	*/
	scope: z.literal("data-plane-database"),
	/** Temp files created in the window. */
	tempFilesDelta: z.number().int().nonnegative(),
	/** Temp bytes written in the window. */
	tempBytesDelta: z.number().int().nonnegative(),
	blksReadDelta: z.number().int().nonnegative(),
	blksHitDelta: z.number().int().nonnegative(),
	/** Buckets with an aggregate row in the window. `positive()`: a read section exists only when at least one row does. */
	bucketsContributing: z.number().int().positive(),
	resetSkippedBuckets: z.number().int().nonnegative(),
	/**
	* Per-bucket `temp_bytes` deltas, oldest first, `window.bucketCount` entries, indexed like
	* `seqScanSeries` and sharing its presence. `null` where the sampler held no bucket at that
	* start; the aggregate row is written every sampled tick, so `0` means no temp bytes (R-019,
	* R-021, R-027).
	*/
	tempBytesSeries: z.array(z.number().int().nonnegative().nullable()).readonly()
});
const AppDatabaseWindowSchema = z.object({
	/**
	* The first bucket's start, aligned down to a 300-second boundary (R-021). ISO 8601 UTC with
	* milliseconds, `Date.prototype.toISOString()`'s form, as every `*At` field on this surface;
	* the index-to-time arithmetic parses it with `Date.parse`.
	*/
	fromIso: z.string().min(1),
	/** `fetchedAt`, the exclusive end of the window, in the same form. */
	toIso: z.string().min(1),
	/** Five minutes, per D11. A literal so a consumer cannot be handed a different bucket size without a contract change. */
	bucketSeconds: z.literal(300),
	/**
	* Bucket starts `b` with `from <= b < to`. Every series in the response has this length (R-021).
	* The window is the 24-hour retention bound, so it holds 288 starts on a boundary and 289
	* otherwise; `max(289)` makes a larger value a producer defect (R-025).
	*/
	bucketCount: z.number().int().positive().max(289),
	/**
	* Buckets the sampler held in this window: rows in the aggregate table, which is written every
	* sampled tick (R-027). `bucketCount - bucketsPresent` is the number of gaps, for every series.
	*/
	bucketsPresent: z.number().int().nonnegative()
});
/**
* Server cap on `tables`. Hitting it sets `truncated`, so the cap is never silent.
*
* ⛔ Declared ABOVE `AppDatabaseObservationResponseSchema` because that schema's `tables` array
* binds it with `.max()` while the module loads. A declaration below the schema is in the
* temporal dead zone at that moment and throws. Keep this the one declaration.
*
* BOTH ends hold the cap (S-2). The DP's `LIMIT` and `slice` never emit more than this, and the
* CP's parse refuses a response that carries more instead of forwarding it to a reader.
*/
const MAX_APP_DATABASE_TABLE_ROWS = 200;
/**
* Three independent sections, each with its own read state. A sampler that has table deltas but
* whose `pg_stat_database` read failed reports `tables` read and `database` unread. One section's
* failure never empties another.
*
* ⛔ No member is optional and none is omitted when empty. `tables` with an empty array means the
* window held buckets and no table moved; that differs from `read: false`, and the distinction is
* the whole point of D3.
*
* The refinement is the R-021 invariant: every series in the response is indexed by the window's
* bucket capacity.
*/
const AppDatabaseObservationResponseSchema = z.object({
	appId: AppIdSchema,
	namespace: K8sNameSchema,
	fetchedAt: z.string().min(1),
	window: sectionSchema(AppDatabaseWindowSchema),
	tables: sectionSchema(z.array(AppDatabaseTableObservationSchema).max(200).readonly()),
	database: sectionSchema(AppDatabaseAggregateSchema),
	/** Set when `tables` hit `MAX_APP_DATABASE_TABLE_ROWS`. Never a silent cap. */
	truncated: z.boolean()
}).superRefine((r, ctx) => {
	if (!r.window.read) return;
	const n = r.window.data.bucketCount;
	if (r.tables.read) r.tables.data.forEach((t, i) => {
		if (t.seqScanSeries.length !== n) ctx.addIssue({
			code: "custom",
			path: [
				"tables",
				"data",
				i,
				"seqScanSeries"
			],
			message: `seqScanSeries has ${t.seqScanSeries.length} entries, window.bucketCount is ${n}`
		});
	});
	if (r.database.read && r.database.data.tempBytesSeries.length !== n) ctx.addIssue({
		code: "custom",
		path: [
			"database",
			"data",
			"tempBytesSeries"
		],
		message: `tempBytesSeries has ${r.database.data.tempBytesSeries.length} entries, window.bucketCount is ${n}`
	});
});
/** The route the DP serves and the CP proxies. */
const APP_DATABASE_OBSERVATION_PATH = "database";
const AppIntrospectionSchemas = {
	AppStreamInfoQuery: AppStreamInfoQuerySchema,
	AppStreamInfoResponse: AppStreamInfoResponseSchema,
	AppDependenciesQuery: AppDependenciesQuerySchema,
	AppDependenciesResponse: AppDependenciesResponseSchema,
	AppConfigQuery: AppConfigQuerySchema,
	AppConfigResponse: AppConfigResponseSchema,
	ProbeAppFixtureRequest: ProbeAppFixtureRequestSchema,
	ProbeAppFixtureResponse: ProbeAppFixtureResponseSchema,
	AppLogsAggregateQuery: AppLogsAggregateQuerySchema,
	AppLogsAggregateResponse: AppLogsAggregateResponseSchema,
	AppDatabaseObservationQuery: AppDatabaseObservationQuerySchema,
	AppDatabaseObservationResponse: AppDatabaseObservationResponseSchema
};
/**
* The query of the three CP middle-hop GET routes `app-streams`,
* `app-dependencies` and `app-database` on provisioning-svc.
*
* Caller: provisioning-svc's `AppIntrospectionController` binds it through
* `@Query({ schema })`. A CP client that builds the query can parse it first.
*
* Input: an object with one key, `appId`, which `AppIdSchema` bounds (1 to 253
* characters of `ID_PART_PATTERN`).
*
* Output: the parsed `{ appId }` for a valid object. The parse fails for these
* input classes:
* - `appId` absent, empty, longer than 253 characters, or outside the charset;
* - a `namespace` key. The route derives the namespace from `:id` through
*   `resolveDpNamespace`, and a caller namespace is a cross-tenant read;
* - any other key, because the schema is `.strict()`. Under
*   `ZionStrictValidationPipe` each failure is a 400.
*
* Invariant: the key set equals the key set of `AppStreamInfoQuerySchema`,
* `AppDependenciesQuerySchema` and `AppDatabaseObservationQuerySchema` without
* `namespace`. The schema derives from `AppStreamInfoQuerySchema` by
* `.omit()`, so the `appId` bound cannot drift from the DP door. It has no
* side effect.
*/
const AppIntrospectionProxyQuerySchema = AppStreamInfoQuerySchema.omit({ namespace: true });
/**
* The query of the CP middle-hop route `GET /v1/data-planes/:id/k8s/app-config`
* on provisioning-svc.
*
* Caller: provisioning-svc's `AppIntrospectionController` binds it through
* `@Query({ schema })`.
*
* Input: `appId` (required, `AppIdSchema`) and `fixtureId` (optional,
* `FixtureIdSchema`, 1 to `MAX_FIXTURE_ID_LENGTH` characters). An absent
* `fixtureId` asks for every fixture of the app.
*
* Output: the parsed `{ appId, fixtureId? }` for a valid object. The parse
* fails for a bad `appId` or `fixtureId`, for a `namespace` key and for any
* other key, because the schema is `.strict()`.
*
* Invariant: the key set equals the key set of `AppConfigQuerySchema` without
* `namespace`, by `.omit()`. It has no side effect.
*/
const AppConfigProxyQuerySchema = AppConfigQuerySchema.omit({ namespace: true });
/**
* The body of the CP middle-hop route
* `POST /v1/data-planes/:id/k8s/app-fixture-probe` on provisioning-svc.
*
* Caller: provisioning-svc's `AppIntrospectionController` binds it through
* `@Body({ schema })`.
*
* Input: `appId` (`AppIdSchema`) and `fixtureId` (`FixtureIdSchema`), both
* required.
*
* Output: the parsed `{ appId, fixtureId }` for a valid object. The parse
* fails for an absent or bad `appId` or `fixtureId`, for a `namespace` key and
* for any other key. That includes every target key that SCH-1 names for
* `ProbeAppFixtureRequestSchema` (`url`, `host`, `port`, `path`, `scheme`,
* `timeoutMs`, `headers` and the rest). This schema is the SSRF fence at the
* CP hop, and the DP derives the probe target from the fixture's own Service.
*
* Invariant: the key set equals the key set of `ProbeAppFixtureRequestSchema`
* without `namespace`, by `.omit()`. It has no side effect.
*/
const ProbeAppFixtureProxyBodySchema = ProbeAppFixtureRequestSchema.omit({ namespace: true });
/**
* The registry of the CP middle-hop request shapes.
*
* It is a second registry, apart from `AppIntrospectionSchemas`, because the
* DP producer contract test (CT-5b in
* `cloud/dp/core-api/test/contract/app-introspection.contract.spec.ts`) pairs
* every `Query` and `Request` key of `AppIntrospectionSchemas` with a
* `Response` key that a DP endpoint returns. These three shapes never reach
* the DP, so they have no DP response, and a member of the first registry
* would fail CT-5b.
*
* It holds exactly three keys, one per schema above. Each value is a
* `.strict()` zod object.
*/
const AppIntrospectionProxySchemas = {
	AppIntrospectionProxyQuery: AppIntrospectionProxyQuerySchema,
	AppConfigProxyQuery: AppConfigProxyQuerySchema,
	ProbeAppFixtureProxyBody: ProbeAppFixtureProxyBodySchema
};
//#endregion
export { APP_DATABASE_OBSERVATION_PATH, AppConfigEntrySchema, AppConfigFixtureSchema, AppConfigProxyQuerySchema, AppConfigQuerySchema, AppConfigResponseSchema, AppConfigSourceRefSchema, AppConfigSourceSchema, AppDatabaseAggregateSchema, AppDatabaseObservationQuerySchema, AppDatabaseObservationResponseSchema, AppDatabaseTableObservationSchema, AppDatabaseWindowSchema, AppDependenciesPrincipalSchema, AppDependenciesQuerySchema, AppDependenciesResponseSchema, AppDependencyKindSchema, AppDependencyRowSchema, AppIdSchema, AppIntrospectionProxyQuerySchema, AppIntrospectionProxySchemas, AppIntrospectionSchemas, AppIntrospectionUnreadReasonSchema, AppLogsAggregateQueryFieldsSchema, AppLogsAggregateQuerySchema, AppLogsAggregateResponseSchema, AppStreamConsumerSchema, AppStreamInfoQuerySchema, AppStreamInfoResponseSchema, AppStreamPrincipalSchema, AppStreamStateSchema, FixtureIdSchema, MAX_APP_CONFIG_ENTRIES_PER_FIXTURE, MAX_APP_CONFIG_FIXTURES, MAX_APP_DATABASE_TABLE_ROWS, MAX_APP_DEPENDENCY_PRINCIPALS, MAX_APP_DEPENDENCY_ROWS_PER_PRINCIPAL, MAX_APP_STREAM_PRINCIPALS, PROBE_MAX_RESPONSE_BYTES, PROBE_TIMEOUT_MS, ProbeAppFixtureOutcomeSchema, ProbeAppFixtureProxyBodySchema, ProbeAppFixtureRequestSchema, ProbeAppFixtureResponseSchema, ProbeErrorSchema, ProbeTargetSchema, StreamRetentionSchema, VERSION_SLUG_REQUIRES_FIXTURE_ID, sectionSchema, versionSlugRequiresFixtureId };

//# sourceMappingURL=app-introspection.schemas.js.map